import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    list_task_events,
    update_task_session_endtime,
    update_task_description,
    update_task_name,
    get_all_members,
    reassignTaskAssignee,
    update_task,
    task_log,
} from '../../../utils/talent_services';
import { Clock, Edit2, Save, X, ChevronLeft, Search, Pause, Play, CheckSquare, Square } from 'lucide-react';
import Modal_proyectos from '../../menus/Modal';
import UserAvatar from '../dashboard/UserAvatar';
import TaskDescriptionView from './TaskDescriptionView';
import TaskDescriptionEditor from './TaskDescriptionEditor';
import { prepareTaskDescriptionHtmlForSave } from '../../../utils/task_description_html';

/** Parámetros para reassignTaskAssignee según datos de la tarea (alineado con reassign_task_assignee.php). */
function deriveReassignParamsFromTaskInfo(info) {
    if (!info) return { strategy_mode: 'none', trello_card_id: '', opportunity_id: '' };
    const opp = Number(info.opportunity_id);
    if (Number.isFinite(opp) && opp > 0) {
        return { strategy_mode: 'opportunity', trello_card_id: '', opportunity_id: String(opp) };
    }
    const trello = info.strategy_trello_card_id != null ? String(info.strategy_trello_card_id).trim() : '';
    const cid = Number(info.card_id);
    if (Number.isFinite(cid) && cid > 0 && trello !== '' && trello !== '99999999') {
        return { strategy_mode: 'trello', trello_card_id: trello, opportunity_id: '' };
    }
    return { strategy_mode: 'none', trello_card_id: '', opportunity_id: '' };
}

function isTaskFlagTrue(value) {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        return v === '1' || v === 'true';
    }
    return false;
}

function taskHasLinkedStrategyCard(meta) {
    if (!meta) return false;
    const cid = meta.card_id;
    if (cid != null && cid !== '' && Number(cid) > 0) return true;
    const tid = meta.strategy_trello_card_id ?? meta.trello_card_id;
    return tid != null && String(tid).trim() !== '' && tid !== '99999999' && tid !== 99999999;
}

function getCardById(cards, targetCardId) {
    if (!Array.isArray(cards) || targetCardId == null || targetCardId === '') return undefined;
    const target = String(targetCardId);
    return cards.find((card) => String(card.cardid) === target);
}

const SESSION_DATE_TIME_FORMAT = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
};

function formatSessionDateTime(dateTimeString) {
    if (!dateTimeString) return 'En curso...';
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) return 'En curso...';
    return date.toLocaleString('es-ES', SESSION_DATE_TIME_FORMAT);
}

function formatSessionDateTimeForEdit(dateTimeStringOrDate) {
    const date =
        dateTimeStringOrDate instanceof Date
            ? dateTimeStringOrDate
            : new Date(dateTimeStringOrDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('es-ES', SESSION_DATE_TIME_FORMAT);
}

function parseSessionDateTimeFromDisplay(text) {
    if (!text || !String(text).trim()) return null;
    const normalized = String(text).replace(/[\u202f\u00a0]/g, ' ').trim();
    const match = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})\s+(.+)$/i);
    if (!match) return null;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    let hour = parseInt(match[4], 10);
    const minute = parseInt(match[5], 10);
    const ampmNorm = match[6].toLowerCase().replace(/[\s.]/g, '');

    if ([day, month, year, hour, minute].some((n) => Number.isNaN(n))) return null;
    if (!ampmNorm.startsWith('a') && !ampmNorm.startsWith('p')) return null;

    if (ampmNorm.startsWith('p') && hour !== 12) hour += 12;
    if (ampmNorm.startsWith('a') && hour === 12) hour = 0;

    const date = new Date(year, month - 1, day, hour, minute, 0, 0);
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return null;
    }
    return date;
}

/**
 * Componente para mostrar estadísticas de una tarea específica
 */
const Statistics = ({ usr_id, token, cards, taskId, isEmbedded = false, onCloseEmbedded, onTaskStateChange }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const task_id_param = searchParams.get('task_id') || ''; 
    const resolvedTaskId = taskId || task_id_param || '';
    
    const [taskEvents, setTaskEvents] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editedEndTime, setEditedEndTime] = useState('');
    const [taskName, setTaskName] = useState('');
    const [taskDescription, setTaskDescription] = useState('');
    const [creatorDisplayName, setCreatorDisplayName] = useState('');
    const [assigneeUsrId, setAssigneeUsrId] = useState(null);
    const [assigneeDisplayName, setAssigneeDisplayName] = useState('');
    const [strategyReassignSource, setStrategyReassignSource] = useState(null);
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const [reassigning, setReassigning] = useState(false);
    const [isEditingTaskName, setIsEditingTaskName] = useState(false);
    const [editingTaskNameValue, setEditingTaskNameValue] = useState('');
    const [savingTaskName, setSavingTaskName] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState('');
    const [savingDescription, setSavingDescription] = useState(false);
    const [taskActive, setTaskActive] = useState(false);
    const [taskCompleted, setTaskCompleted] = useState(false);
    const [taskUpdateMeta, setTaskUpdateMeta] = useState(null);
    const [togglingExecution, setTogglingExecution] = useState(false);

    const taskStatusKey = useMemo(() => {
        if (taskCompleted) return 'completed';
        if (taskActive) return 'running';
        return 'pending';
    }, [taskActive, taskCompleted]);

    // --- Custom CSS similar a Tacticfollowup ---
    const customStyles = `
        .statistics-container {
            background-color: #fff;
            border: 1px solid #e0e0e0;
            border-radius: 0.5rem;
            padding: 2rem;
            margin-top: 2rem;
        }
        .statistics-header {
            font-size: 1.5rem;
            font-weight: 600;
            color: #4b5563;
            margin-bottom: 1.5rem;
            text-align: center;
        }
        .stat-item {
            padding: 1rem;
            margin-bottom: 1rem;
            background-color: #f9fafb;
            border-radius: 0.5rem;
            border-left: 4px solid #6366f1;
        }
        .stat-label {
            font-size: 0.875rem;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 0.5rem;
        }
        .stat-value {
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
        }
        .session-card {
            background-color: #fff;
            border: 1px solid #e0e0e0;
            border-radius: 0.5rem;
            padding: 1.5rem;
            margin-bottom: 1rem;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .session-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
        }
        .session-title {
            font-size: 1.125rem;
            font-weight: 600;
            color: #1f2937;
        }
        .session-time {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }
        .time-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .time-label {
            font-size: 0.875rem;
            color: #6b7280;
            min-width: 80px;
        }
        .time-value {
            font-size: 0.9375rem;
            color: #1f2937;
            font-weight: 500;
        }
        .edit-time-input {
            padding: 0.375rem 0.75rem;
            border: 1px solid #d1d5db;
            border-radius: 0.375rem;
            font-size: 0.9375rem;
        }
        .edit-buttons {
            display: flex;
            gap: 0.5rem;
            margin-top: 0.5rem;
        }
        .btn-edit {
            padding: 0.375rem 0.75rem;
            border: none;
            border-radius: 0.375rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.25rem;
            font-size: 0.875rem;
        }
        .btn-edit-primary {
            background-color: #6366f1;
            color: white;
        }
        .btn-edit-primary:hover {
            background-color: #4f46e5;
        }
        .btn-edit-secondary {
            background-color: #6b7280;
            color: white;
        }
        .btn-edit-secondary:hover {
            background-color: #4b5563;
        }
        .btn-edit-icon {
            padding: 0.25rem;
            background-color: transparent;
            border: none;
            cursor: pointer;
            color: #6366f1;
        }
        .btn-edit-icon:hover {
            color: #4f46e5;
        }
        .error-message {
            color: #ef4444;
            font-size: 0.875rem;
            margin-top: 0.25rem;
        }
        .task-header-card {
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            color: white;
            border-radius: 0.5rem;
            padding: 1.5rem;
            margin-bottom: 2rem;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .task-header-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
        }
        .task-title-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .task-title-text {
            overflow-wrap: anywhere;
        }
        .task-title-edit-actions {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            margin-left: auto;
        }
        .task-title-edit-input {
            flex: 1;
            min-width: 120px;
            border: 1px solid rgba(255,255,255,0.6);
            border-radius: 0.375rem;
            padding: 0.25rem 0.5rem;
            background: rgba(255,255,255,0.15);
            color: white;
            font-size: 1rem;
        }
        .task-title-edit-input::placeholder {
            color: rgba(255,255,255,0.75);
        }
        .btn-task-title-edit {
            padding: 0.25rem;
            border: 1px solid rgba(255,255,255,0.45);
            border-radius: 0.375rem;
            background: rgba(255,255,255,0.15);
            color: white;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .btn-task-title-edit:hover {
            background: rgba(255,255,255,0.25);
        }
        .task-header-time {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.5rem;
            font-size: 1.125rem;
        }
        .task-header-creator {
            font-size: 1rem;
            font-weight: 500;
            opacity: 0.95;
            margin-left: 0.25rem;
        }
        .task-description-block {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid rgba(255,255,255,0.3);
        }
        .task-description-view {
            font-size: 0.9375rem;
            line-height: 1.5;
            opacity: 0.95;
            min-height: 1.5em;
        }
        .task-description-view h2 {
            font-size: 1.125rem;
            font-weight: 600;
            margin: 0.5rem 0 0.25rem;
        }
        .task-description-view h3 {
            font-size: 1rem;
            font-weight: 600;
            margin: 0.35rem 0 0.2rem;
        }
        .task-description-view p {
            margin: 0 0 0.35rem;
        }
        .task-description-view ul {
            margin: 0.25rem 0 0 1rem;
            padding-left: 0.5rem;
        }
        .task-description-view input[type="checkbox"] {
            margin-right: 0.35rem;
            vertical-align: middle;
            cursor: pointer;
        }
        .task-description-view input[type="checkbox"]:checked {
            accent-color: #10b981;
        }
        .task-description-view a.task-description-link {
            color: #e0e7ff;
            text-decoration: underline;
            text-underline-offset: 0.12em;
            word-break: break-all;
        }
        .task-description-view a.task-description-link:hover {
            color: #fff;
        }
        .task-description-view ul.task-desc-checklist {
            list-style: none;
            margin-left: 0;
            padding-left: 0;
        }
        .task-description-view ul.task-desc-checklist li {
            display: flex;
            align-items: center;
            margin-bottom: 0.25rem;
            min-height: 1.5em;
        }
        .task-description-view ul.task-desc-checklist li input[type="checkbox"] {
            margin-right: 0.35rem;
            flex-shrink: 0;
            vertical-align: middle;
            margin-top: 0;
            margin-bottom: 0;
        }
        .task-description-view ul.task-desc-checklist li input[type="checkbox"]:checked {
            accent-color: #10b981;
        }
        .btn-desc-edit {
            padding: 0.25rem 0.5rem;
            border: 1px solid rgba(255,255,255,0.5);
            border-radius: 0.375rem;
            background: rgba(255,255,255,0.15);
            color: white;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            font-size: 0.8125rem;
            margin-top: 0.5rem;
        }
        .btn-desc-edit:hover {
            background: rgba(255,255,255,0.25);
        }

        /* Mobile: quitar márgenes laterales del encabezado morado
           para que la descripción tenga más ancho para escribir. */
        @media (max-width: 767.98px) {
            .statistics-container {
                padding: 1rem;
                margin-top: 1.25rem;
            }
            .task-header-card {
                padding: 1rem 0.75rem;
            }
            .task-description-block {
                margin-top: 0.75rem;
                padding-top: 0.75rem;
            }
        }
        .task-header-assignee-wrap {
            display: inline-flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.35rem;
            margin-left: 0.25rem;
        }
        .task-header-assignee-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            padding: 0.15rem 0.35rem 0.15rem 0.15rem;
            border: 1px solid rgba(255,255,255,0.45);
            border-radius: 0.375rem;
            background: rgba(255,255,255,0.15);
            color: white;
            cursor: pointer;
            font-size: 1rem;
            font-weight: 500;
        }
        .task-header-assignee-btn:hover {
            background: rgba(255,255,255,0.25);
        }
        .task-header-assignee-btn .avatar-container .profile-pic-circle,
        .task-header-assignee-btn .avatar-container .initials-circle {
            width: 28px;
            height: 28px;
        }
        .task-status-bar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem;
            margin-top: 0.75rem;
            padding: 0.65rem 0.85rem;
            background: rgba(255,255,255,0.12);
            border-radius: 0.5rem;
            border: 1px solid rgba(255,255,255,0.22);
        }
        .task-status-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.45rem;
            font-size: 0.875rem;
            font-weight: 600;
            letter-spacing: 0.01em;
        }
        .task-status-dot {
            width: 0.55rem;
            height: 0.55rem;
            border-radius: 50%;
            flex-shrink: 0;
        }
        .task-status-badge--running .task-status-dot {
            background: #4ade80;
            box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.35);
            animation: task-status-pulse 1.6s ease-in-out infinite;
        }
        .task-status-badge--pending .task-status-dot {
            background: rgba(255,255,255,0.55);
        }
        .task-status-badge--completed .task-status-dot {
            background: #86efac;
        }
        @keyframes task-status-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.55; transform: scale(0.88); }
        }
        .task-status-actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
        }
        .task-status-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            padding: 0.35rem 0.7rem;
            border-radius: 0.375rem;
            border: 1px solid rgba(255,255,255,0.45);
            background: rgba(255,255,255,0.18);
            color: white;
            font-size: 0.8125rem;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.15s ease;
        }
        .task-status-btn:hover:not(:disabled) {
            background: rgba(255,255,255,0.28);
        }
        .task-status-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }
        .task-status-btn--running {
            border-color: rgba(248, 113, 113, 0.65);
            background: rgba(248, 113, 113, 0.22);
        }
        .task-status-btn--running:hover:not(:disabled) {
            background: rgba(248, 113, 113, 0.32);
        }
        .task-status-btn--complete {
            border-color: rgba(134, 239, 172, 0.55);
        }
    `;

    useEffect(() => {
        fetch_list_task_events();
    }, [token, resolvedTaskId, navigate]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            const list = await get_all_members(token);
            if (cancelled) return;
            if (list === 'INVALID') return;
            if (!Array.isArray(list)) return;
            const normalized = list
                .map((row) => {
                    const rawId = row.usr_id;
                    const uid = rawId != null && rawId !== '' ? Number(rawId) : null;
                    const nombre = row.nombre ?? row.Nombre;
                    const displayName =
                        (row.team_usr_name && String(row.team_usr_name).trim()) ||
                        (nombre && String(nombre).trim()) ||
                        (row.usr_name && String(row.usr_name).trim()) ||
                        (row.email && String(row.email).trim()) ||
                        'Usuario';
                    return { usr_id: uid, displayName, email: row.email || '' };
                })
                .filter((x) => x.usr_id != null && !Number.isNaN(x.usr_id));
            const selfNum = usr_id != null ? Number(usr_id) : null;
            if (selfNum != null && !Number.isNaN(selfNum) && !normalized.some((x) => x.usr_id === selfNum)) {
                normalized.unshift({ usr_id: selfNum, displayName: 'Tú', email: '' });
            }
            const seen = new Set();
            const deduped = [];
            for (const row of normalized) {
                if (seen.has(row.usr_id)) continue;
                seen.add(row.usr_id);
                deduped.push(row);
            }
            setAssigneeOptions(deduped);
        })();
        return () => {
            cancelled = true;
        };
    }, [token, usr_id]);

    const filteredAssigneeOptions = useMemo(() => {
        const q = assigneeSearch.trim().toLowerCase();
        if (!q) return assigneeOptions;
        return assigneeOptions.filter(
            (o) =>
                (o.displayName && o.displayName.toLowerCase().includes(q)) ||
                (o.email && String(o.email).toLowerCase().includes(q))
        );
    }, [assigneeOptions, assigneeSearch]);

    const assigneeLabelResolved = useMemo(() => {
        if (assigneeDisplayName && String(assigneeDisplayName).trim()) return String(assigneeDisplayName).trim();
        if (assigneeUsrId != null) {
            const o = assigneeOptions.find((x) => x.usr_id === assigneeUsrId);
            if (o) return o.displayName;
            return `Usuario ${assigneeUsrId}`;
        }
        return 'Sin asignar';
    }, [assigneeDisplayName, assigneeUsrId, assigneeOptions]);

    async function fetch_list_task_events(options = {}) {
        const silent = options.silent === true;
        if (!resolvedTaskId) {
            setTaskEvents([]);
            setSessions([]);
            setTaskName('');
            setTaskDescription('');
            setCreatorDisplayName('');
            setAssigneeUsrId(null);
            setAssigneeDisplayName('');
            setStrategyReassignSource(null);
            setTaskActive(false);
            setTaskCompleted(false);
            setTaskUpdateMeta(null);
            setLoading(false);
            return;
        }
        if (!silent) setLoading(true);
        setAssigneeUsrId(null);
        setAssigneeDisplayName('');
        setStrategyReassignSource(null);
        const task_list = await list_task_events(token, resolvedTaskId);
        if (task_list == null) {
            navigate("/");
        } else {
            if (task_list != null && task_list['task_events']) {
                const events = task_list['task_events'].map((celement, index) => {
                    return {
                        id: celement.id,
                        usr_id: celement.usr_id,
                        task_id: celement.task_id,
                        event: celement.event,
                        date_time: celement.date_time,
                        index: index
                    };
                });
                
                setTaskEvents(events);

                // Extraer nombre de la tarea y descripción
                if (task_list['task_info'] && task_list['task_info'].length > 0) {
                    const taskInfo = task_list['task_info'][0];
                    setTaskName(taskInfo.task_name || '');
                    setTaskDescription(taskInfo.task_description || '');
                    const creatorLabel =
                        (taskInfo.creator_usr_name && String(taskInfo.creator_usr_name).trim()) ||
                        (taskInfo.creator_email && String(taskInfo.creator_email).trim()) ||
                        '';
                    setCreatorDisplayName(creatorLabel);
                    const rawAssignee = taskInfo.assignee_usr_id;
                    const assigneeNum =
                        rawAssignee != null && rawAssignee !== '' ? Number(rawAssignee) : null;
                    if (assigneeNum != null && Number.isFinite(assigneeNum) && assigneeNum > 0) {
                        setAssigneeUsrId(assigneeNum);
                    } else {
                        setAssigneeUsrId(null);
                    }
                    const assigneeLabel =
                        (taskInfo.assignee_usr_name && String(taskInfo.assignee_usr_name).trim()) ||
                        (taskInfo.assignee_email && String(taskInfo.assignee_email).trim()) ||
                        '';
                    setAssigneeDisplayName(assigneeLabel);
                    setStrategyReassignSource({
                        opportunity_id: taskInfo.opportunity_id,
                        card_id: taskInfo.card_id,
                        strategy_trello_card_id: taskInfo.strategy_trello_card_id,
                    });
                    setTaskActive(isTaskFlagTrue(taskInfo.active));
                    setTaskCompleted(isTaskFlagTrue(taskInfo.completed));
                    setTaskUpdateMeta({
                        card_id: taskInfo.card_id,
                        opportunity_id: taskInfo.opportunity_id,
                        strategy_trello_card_id: taskInfo.strategy_trello_card_id,
                    });
                } else {
                    setCreatorDisplayName('');
                    setAssigneeUsrId(null);
                    setAssigneeDisplayName('');
                    setStrategyReassignSource(null);
                    setTaskActive(false);
                    setTaskCompleted(false);
                    setTaskUpdateMeta(null);
                }
                
                // Procesar eventos para crear sesiones
                const processedSessions = processEventsIntoSessions(events);
                setSessions(processedSessions);
        
                if (!silent) setLoading(false);
            }
        }
    }

    // Función para procesar eventos y crear sesiones
    function processEventsIntoSessions(events) {
        const sessions = [];
        let currentActivated = null;
        const reopenedEvents = []; // Almacenar eventos reopened para asociarlos después
        let standAloneCompletedEvent = null; // Para eventos completed sin activated previo

        // Primero, procesar todos los eventos excepto reopened
        for (let i = 0; i < events.length; i++) {
            const event = events[i];
            // Normalizar el valor del evento (trim y lowercase para asegurar comparación correcta)
            const eventType = (event.event || '').toString().trim().toLowerCase();
            
            if (eventType === 'activated') {
                currentActivated = event;
            } else if (eventType === 'deactivated' || eventType === 'completed') {
                if (currentActivated) {
                    sessions.push({
                        id: `session-${currentActivated.id}-${event.id}`,
                        startEvent: currentActivated,
                        endEvent: event,
                        startTime: currentActivated.date_time,
                        endTime: event.date_time,
                        status: eventType, // Usar el valor normalizado
                        originalEndTime: event.date_time,
                        endEventId: event.id,
                        usr_id: currentActivated.usr_id,
                        reopenedEvents: [] // Inicializar array para eventos reopened
                    });
                    currentActivated = null;
                } else if (eventType === 'completed') {
                    // Evento completed sin activated previo - guardar para procesarlo después
                    standAloneCompletedEvent = event;
                }
            } else if (eventType === 'reopened') {
                reopenedEvents.push(event);
            }
        }

        // Si hay un activated sin cerrar, crear sesión abierta
        if (currentActivated) {
            sessions.push({
                id: `session-${currentActivated.id}-open`,
                startEvent: currentActivated,
                endEvent: null,
                startTime: currentActivated.date_time,
                endTime: null,
                status: 'open',
                originalEndTime: null,
                endEventId: null,
                usr_id: currentActivated.usr_id,
                reopenedEvents: []
            });
        }

        // Si hay un evento completed sin activated previo, marcar la última sesión como completada
        if (standAloneCompletedEvent && sessions.length > 0) {
            const lastSession = sessions[sessions.length - 1];
            lastSession.status = 'completed';
            lastSession.completedEvent = standAloneCompletedEvent;
        }

        // Asociar eventos reopened con las sesiones correspondientes
        // Un reopened se asocia con la sesión que fue cerrada justo antes del reopened
        for (let i = 0; i < reopenedEvents.length; i++) {
            const reopenedEvent = reopenedEvents[i];
            const reopenedTime = new Date(reopenedEvent.date_time);
            
            // Buscar la sesión cerrada más reciente antes del evento reopened
            let closestSession = null;
            let closestTimeDiff = Infinity;
            
            for (let j = 0; j < sessions.length; j++) {
                const session = sessions[j];
                if (session.endTime) {
                    const sessionEndTime = new Date(session.endTime);
                    if (sessionEndTime < reopenedTime) {
                        const timeDiff = reopenedTime - sessionEndTime;
                        if (timeDiff < closestTimeDiff) {
                            closestTimeDiff = timeDiff;
                            closestSession = session;
                        }
                    }
                }
            }
            
            // Asociar el reopened con la sesión más cercana encontrada
            if (closestSession) {
                closestSession.reopenedEvents.push(reopenedEvent);
            }
        }

        return sessions;
    }

    // Función para calcular duración
    function calculateDuration(startTime, endTime) {
        if (!startTime || !endTime) return 'En curso...';
        const start = new Date(startTime);
        const end = new Date(endTime);
        const diffMs = end - start;
        const diffMins = Math.floor(diffMs / 60000);
        const hours = Math.floor(diffMins / 60);
        const minutes = diffMins % 60;
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    }

    // Función para calcular el tiempo total dedicado a la tarea
    function calculateTotalTime(sessions) {
        let totalMs = 0;
        
        sessions.forEach(session => {
            const start = new Date(session.startTime);
            // Si la sesión está abierta, usar la fecha actual
            const end = session.endTime ? new Date(session.endTime) : new Date();
            const diffMs = end - start;
            if (diffMs > 0) {
                totalMs += diffMs;
            }
        });
        
        const totalMins = Math.floor(totalMs / 60000);
        const hours = Math.floor(totalMins / 60);
        const minutes = totalMins % 60;
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    }

    // Función para iniciar edición
    function handleStartEdit(session) {
        setEditingSessionId(session.id);
        setEditedEndTime(
            session.endTime
                ? formatSessionDateTimeForEdit(session.endTime)
                : formatSessionDateTimeForEdit(new Date())
        );
    }

    // Función para cancelar edición
    function handleCancelEdit() {
        setEditingSessionId(null);
        setEditedEndTime('');
    }

    // Función para convertir Date a formato MySQL sin cambiar zona horaria
    function formatDateForMySQL(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    // Función para guardar edición
    function handleSaveEdit(session) {
        if (!editedEndTime) {
            alert('Por favor ingrese una fecha y hora de finalización');
            return;
        }

        const editedDate = parseSessionDateTimeFromDisplay(editedEndTime);
        if (!editedDate) {
            alert('Por favor ingrese una fecha y hora válida (ej: 19/06/2026, 6:35 p. m.)');
            return;
        }

        const now = new Date();

        // Validar que la fecha editada sea menor a la actual
        if (editedDate >= now) {
            alert('La fecha y hora de finalización debe ser menor a la fecha y hora actual');
            return;
        }

        // Validar que la fecha editada sea mayor a la fecha de inicio
        const startDate = new Date(session.startTime);
        if (editedDate <= startDate) {
            alert('La fecha y hora de finalización debe ser mayor a la fecha de inicio');
            return;
        }

        // Formatear fecha para MySQL manteniendo hora local
        const mysqlDateTime = formatDateForMySQL(editedDate);

        // Actualizar la sesión
        const updatedSessions = sessions.map(s => {
            if (s.id === session.id) {
                return {
                    ...s,
                    endTime: mysqlDateTime,
                    originalEndTime: mysqlDateTime
                };
            }
            return s;
        });

        setSessions(updatedSessions);
        setEditingSessionId(null);
        setEditedEndTime('');
        
        // Enviar al servidor con formato MySQL
        updateTaskEventEndTime(session.endEventId, mysqlDateTime);

    }

    async function updateTaskEventEndTime(event_id, newEndTime) {
        const task_list = await update_task_session_endtime(token, event_id, newEndTime);
        if (task_list == null) {
            navigate("/");
        } else {
            fetch_list_task_events();
        }
    }

    function handleStartEditDescription() {
        setDescriptionDraft(taskDescription || '');
        setIsEditingDescription(true);
    }

    function handleStartEditTaskName() {
        setEditingTaskNameValue(taskName || '');
        setIsEditingTaskName(true);
    }

    function handleCancelEditTaskName() {
        setIsEditingTaskName(false);
        setEditingTaskNameValue(taskName || '');
    }

    async function handleSaveTaskName() {
        const cleanedName = String(editingTaskNameValue || '').trim();
        if (!cleanedName) {
            alert('El nombre de la tarea no puede estar vacio.');
            return;
        }
        if (!resolvedTaskId) return;
        setSavingTaskName(true);
        const result = await update_task_name(token, resolvedTaskId, cleanedName);
        setSavingTaskName(false);
        if (result == null) {
            navigate("/");
            return;
        }
        if (result && (result.ok || result === 1 || result === '1')) {
            setTaskName(cleanedName);
            setIsEditingTaskName(false);
            fetch_list_task_events();
        } else {
            alert('No se pudo guardar el nombre de la tarea.');
        }
    }

    function handleTaskNameEditorKeyDown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveTaskName();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelEditTaskName();
        }
    }

    function handleCancelEditDescription() {
        setIsEditingDescription(false);
        setDescriptionDraft('');
    }

    async function handleDescriptionCheckboxChange(html) {
        const prepared = prepareTaskDescriptionHtmlForSave(html);
        setTaskDescription(prepared);
        update_task_description(token, resolvedTaskId, prepared);
    }

    async function handleSaveDescription(rawHtmlFromEditor) {
        if (!resolvedTaskId) return;
        const sourceHtml =
            typeof rawHtmlFromEditor === 'string' ? rawHtmlFromEditor : descriptionDraft;
        const html = prepareTaskDescriptionHtmlForSave(sourceHtml);
        setSavingDescription(true);
        const result = await update_task_description(token, resolvedTaskId, html);
        setSavingDescription(false);
        if (result == null) {
            navigate('/');
            return;
        }
        if (result && result.ok) {
            setIsEditingDescription(false);
            setDescriptionDraft('');
            await fetch_list_task_events({ silent: true });
        } else {
            const msg =
                result?.message === 'not_found'
                    ? 'No se encontró la tarea.'
                    : result?.message === 'denied'
                      ? 'No tienes permiso para editar esta tarea.'
                      : 'No se pudo guardar la descripción.';
            alert(msg);
        }
    }

    function buildTaskPayload(overrides = {}) {
        const trello =
            taskUpdateMeta?.strategy_trello_card_id != null
                ? String(taskUpdateMeta.strategy_trello_card_id).trim()
                : '';
        const opp = taskUpdateMeta?.opportunity_id;
        const cardId = taskUpdateMeta?.card_id;
        return {
            text: taskName || '',
            completed: overrides.completed ?? taskCompleted,
            id: resolvedTaskId,
            active: overrides.active ?? taskActive,
            trello_card_id: trello,
            opportunity_id: opp != null && opp !== '' ? String(opp) : '',
            card_id: cardId != null && cardId !== '' ? cardId : null,
        };
    }

    async function persistTaskUpdate(payload) {
        const result = await update_task(
            token,
            payload.text,
            payload.completed,
            payload.id,
            payload.active,
            payload.trello_card_id,
            payload.opportunity_id,
            payload.card_id
        );
        if (result == null) {
            navigate('/');
            return false;
        }
        return true;
    }

    async function registerTaskLog(logType, additionalInfo = '', taskNameForLog = '') {
        const res = await task_log(token, resolvedTaskId, logType, additionalInfo, taskNameForLog);
        if (res == null) navigate('/');
    }

    async function handleToggleActive() {
        if (!resolvedTaskId || !usr_id || togglingExecution || taskCompleted) return;
        const newActive = !taskActive;
        setTogglingExecution(true);
        const payload = buildTaskPayload({ active: newActive });
        const ok = await persistTaskUpdate(payload);
        if (ok) {
            setTaskActive(newActive);
            await registerTaskLog(newActive ? 'activated' : 'deactivated', '', payload.text);
            await fetch_list_task_events({ silent: true });
            if (typeof onTaskStateChange === 'function') onTaskStateChange();
        }
        setTogglingExecution(false);
    }

    async function handleToggleComplete() {
        if (!resolvedTaskId || !usr_id || togglingExecution) return;
        const newCompleted = !taskCompleted;
        setTogglingExecution(true);
        const newActive = newCompleted ? false : taskActive;
        const payload = buildTaskPayload({ completed: newCompleted, active: newActive });
        const ok = await persistTaskUpdate(payload);
        if (ok) {
            setTaskCompleted(newCompleted);
            setTaskActive(newActive);
            if (newCompleted) {
                const cardId = payload.trello_card_id;
                const noCard =
                    cardId == null || cardId === '' || cardId === 99999999 || cardId === '99999999';
                if (noCard) {
                    await registerTaskLog('completed', '', payload.text);
                } else {
                    const card = getCardById(cards, cardId);
                    await registerTaskLog(
                        'completed',
                        card ? JSON.stringify(card.members) : '',
                        payload.text
                    );
                }
            } else {
                await registerTaskLog('reopened');
            }
            await fetch_list_task_events({ silent: true });
            if (typeof onTaskStateChange === 'function') onTaskStateChange();
        }
        setTogglingExecution(false);
    }

    async function handleSelectReassignMember(opt) {
        if (!resolvedTaskId || reassigning) return;
        const params = deriveReassignParamsFromTaskInfo(strategyReassignSource);
        setReassigning(true);
        const res = await reassignTaskAssignee(
            token,
            resolvedTaskId,
            opt.usr_id,
            params.strategy_mode,
            params.trello_card_id,
            params.opportunity_id
        );
        setReassigning(false);
        if (res === 'INVALID' || res == null) {
            navigate('/');
            return;
        }
        if (typeof res === 'object' && res.ok !== true) {
            const msg =
                res.message === 'strategy_mismatch'
                    ? 'La tarea no coincide con el contexto de estrategia. Vuelve a cargar o reasigna desde el tablero táctico.'
                    : 'No se pudo reasignar la tarea.';
            window.alert(msg);
            return;
        }
        setAssigneePickerOpen(false);
        setAssigneeSearch('');
        fetch_list_task_events();
    }

    // --- RENDERIZADO PRINCIPAL ---
    return (
        <div className="container-fluid px-0 px-md-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            {/* Inyectamos estilos personalizados */}
            <style>{customStyles}</style>

            <div className="row justify-content-center">
                <div className="col-12 col-md-10 col-lg-8">
             

                    {loading ? (
                        <div className="text-center mt-5">
                            <p className="text-muted">Cargando sesiones...</p>
                        </div>
                    ) : resolvedTaskId ? (
                        <div className="statistics-container">
                            {/* Encabezado con nombre de tarea y tiempo total (siempre que haya tarea cargada) */}
                            {(taskName || resolvedTaskId) && (
                                <div className="task-header-card">
                                    <div className="task-header-title task-title-row">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (isEmbedded && onCloseEmbedded) {
                                                    onCloseEmbedded();
                                                    return;
                                                }
                                                navigate(-1);
                                            }}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0.25rem',
                                                background: 'rgba(255,255,255,0.2)',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                color: 'white',
                                                cursor: 'pointer',
                                            }}
                                            title="Volver"
                                            aria-label="Volver"
                                        >
                                            <ChevronLeft size={24} />
                                        </button>
                                        {isEditingTaskName ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="task-title-edit-input"
                                                    value={editingTaskNameValue}
                                                    onChange={(e) => setEditingTaskNameValue(e.target.value)}
                                                    onKeyDown={handleTaskNameEditorKeyDown}
                                                    maxLength={255}
                                                    autoFocus
                                                    aria-label="Editar nombre de la tarea"
                                                />
                                                <div className="task-title-edit-actions">
                                                    <button
                                                        type="button"
                                                        className="btn-task-title-edit"
                                                        onClick={handleSaveTaskName}
                                                        title="Guardar nombre"
                                                        aria-label="Guardar nombre"
                                                        disabled={savingTaskName}
                                                    >
                                                        <Save size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-task-title-edit"
                                                        onClick={handleCancelEditTaskName}
                                                        title="Cancelar edicion"
                                                        aria-label="Cancelar edicion"
                                                        disabled={savingTaskName}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="task-title-text">{taskName || `Tarea (ID: ${resolvedTaskId})`}</span>
                                                {usr_id && (
                                                    <button
                                                        type="button"
                                                        className="btn-task-title-edit"
                                                        onClick={handleStartEditTaskName}
                                                        title="Editar nombre"
                                                        aria-label="Editar nombre de la tarea"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {resolvedTaskId && (
                                        <div className="task-status-bar" role="group" aria-label="Estado de ejecución de la tarea">
                                            <span
                                                className={`task-status-badge task-status-badge--${
                                                    taskStatusKey === 'running'
                                                        ? 'running'
                                                        : taskStatusKey === 'completed'
                                                          ? 'completed'
                                                          : 'pending'
                                                }`}
                                            >
                                                <span className="task-status-dot" aria-hidden="true" />
                                                {taskStatusKey === 'running'
                                                    ? 'En ejecución'
                                                    : taskStatusKey === 'completed'
                                                      ? 'Terminada'
                                                      : 'Pausada'}
                                            </span>
                                            {usr_id ? (
                                                <div className="task-status-actions">
                                                    {!taskCompleted ? (
                                                        <button
                                                            type="button"
                                                            className={`task-status-btn${
                                                                taskActive ? ' task-status-btn--running' : ''
                                                            }`}
                                                            onClick={handleToggleActive}
                                                            disabled={togglingExecution}
                                                            aria-label={
                                                                taskActive
                                                                    ? 'Pausar ejecución'
                                                                    : 'Poner en ejecución'
                                                            }
                                                        >
                                                            {taskActive ? (
                                                                <>
                                                                    <Pause size={16} fill="currentColor" />
                                                                    Pausar
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Play size={16} fill="currentColor" />
                                                                    Iniciar
                                                                </>
                                                            )}
                                                        </button>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        className="task-status-btn task-status-btn--complete"
                                                        onClick={handleToggleComplete}
                                                        disabled={togglingExecution}
                                                        aria-label={
                                                            taskCompleted
                                                                ? 'Marcar como pendiente'
                                                                : 'Marcar como terminada'
                                                        }
                                                    >
                                                        {taskCompleted ? (
                                                            <>
                                                                <Square size={16} />
                                                                Reabrir
                                                            </>
                                                        ) : (
                                                            <>
                                                                <CheckSquare size={16} />
                                                                Terminar
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                    {(sessions.length > 0 || creatorDisplayName || resolvedTaskId) && (
                                        <div className="task-header-time">
                                            {sessions.length > 0 && (
                                                <>
                                                    <Clock size={20} />
                                                    <span>Tiempo total dedicado: {calculateTotalTime(sessions)}</span>
                                                </>
                                            )}
                                            {creatorDisplayName ? (
                                                <span className="task-header-creator">
                                                    {sessions.length > 0 ? (
                                                        <span aria-hidden="true"> · </span>
                                                    ) : null}
                                                    Creador: {creatorDisplayName}
                                                </span>
                                            ) : null}
                                            <span className="task-header-assignee-wrap">
                                                {(sessions.length > 0 || creatorDisplayName) ? (
                                                    <span aria-hidden="true"> · </span>
                                                ) : null}
                                                <span className="task-header-creator">Responsable:</span>
                                                {usr_id ? (
                                                    <button
                                                        type="button"
                                                        className="task-header-assignee-btn"
                                                        onClick={() => {
                                                            setAssigneeSearch('');
                                                            setAssigneePickerOpen(true);
                                                        }}
                                                        title="Cambiar responsable de la tarea"
                                                        disabled={reassigning}
                                                        aria-label="Cambiar responsable de la tarea"
                                                    >
                                                        <UserAvatar usr_id={assigneeUsrId} usr_name={assigneeLabelResolved} size={28} />
                                                        <span className="text-truncate" style={{ maxWidth: '12rem' }}>
                                                            {assigneeLabelResolved}
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <>
                                                        <UserAvatar usr_id={assigneeUsrId} usr_name={assigneeLabelResolved} size={28} />
                                                        <span className="task-header-creator">{assigneeLabelResolved}</span>
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    <div className="task-description-block">
                                        {!isEditingDescription ? (
                                            <>
                                                {taskDescription ? (
                                                    <TaskDescriptionView
                                                        html={taskDescription}
                                                        editableCheckboxes={!!usr_id}
                                                        onCheckboxChange={handleDescriptionCheckboxChange}
                                                    />
                                                ) : (
                                                    <div className="task-description-view" style={{ opacity: 0.8 }}>
                                                        Sin descripción
                                                    </div>
                                                )}
                                                {usr_id && (
                                                    <button
                                                        type="button"
                                                        className="btn-desc-edit"
                                                        onClick={handleStartEditDescription}
                                                    >
                                                        <Edit2 size={14} />
                                                        {taskDescription ? 'Editar descripción' : 'Agregar descripción'}
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <TaskDescriptionEditor
                                                key={`desc-edit-${resolvedTaskId}`}
                                                editorKey={`desc-edit-${resolvedTaskId}`}
                                                value={descriptionDraft}
                                                onChange={setDescriptionDraft}
                                                onSave={handleSaveDescription}
                                                onCancel={handleCancelEditDescription}
                                                saving={savingDescription}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}

                            {sessions.length > 0 ? (
                                <>
                                    {/* Encabezado */}
                                    <div className="statistics-header">
                                        Sesiones de Trabajo
                                    </div>

                                    {/* Tarjetas de Sesión */}
                                    <div>
                                        {sessions.map((session, index) => (
                                    <div key={session.id} className="session-card">
                                        <div className="session-header">
                                            <div className="session-title">
                                                Sesión {index + 1}
                                            </div>
                                            <div style={{ 
                                                padding: '0.25rem 0.75rem', 
                                                borderRadius: '0.375rem',
                                                fontSize: '0.875rem',
                                                backgroundColor: (session.status || '').toString().toLowerCase() === 'completed' ? '#10b981' : 
                                                               (session.status || '').toString().toLowerCase() === 'deactivated' ? '#f59e0b' : '#6366f1',
                                                color: 'white',
                                                fontWeight: 500
                                            }}>
                                                {(session.status || '').toString().toLowerCase() === 'completed' ? 'Completada' : 
                                                 (session.status || '').toString().toLowerCase() === 'deactivated' ? 'Pausada' : 'Abierta'}
                                            </div>
                                        </div>

                                        <div className="session-time">
                                            <div className="time-row">
                                                <span className="time-label">Inicio:</span>
                                                <span className="time-value">
                                                    {formatSessionDateTime(session.startTime)}
                                                </span>
                                            </div>

                                            <div className="time-row">
                                                <span className="time-label">Fin:</span>
                                                {editingSessionId === session.id ? (
                                                    <div style={{ flex: 1 }}>
                                                        <input
                                                            type="text"
                                                            className="edit-time-input"
                                                            value={editedEndTime}
                                                            onChange={(e) => setEditedEndTime(e.target.value)}
                                                            placeholder="19/06/2026, 6:35 p. m."
                                                            aria-label="Fecha y hora de finalización"
                                                        />
                                                        <div className="edit-buttons">
                                                            <button
                                                                className="btn-edit btn-edit-primary"
                                                                onClick={() => handleSaveEdit(session)}
                                                            >
                                                                <Save size={14} />
                                                                Guardar
                                                            </button>
                                                            <button
                                                                className="btn-edit btn-edit-secondary"
                                                                onClick={handleCancelEdit}
                                                            >
                                                                <X size={14} />
                                                                Cancelar
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <span className="time-value">
                                                            {formatSessionDateTime(session.endTime)}
                                                        </span>
                                                        {session.endTime && session.usr_id == usr_id && (
                                                            <button
                                                                className="btn-edit-icon"
                                                                onClick={() => handleStartEdit(session)}
                                                                title="Editar hora de finalización"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            <div className="time-row">
                                                <span className="time-label">Duración:</span>
                                                <span className="time-value">
                                                    {calculateDuration(session.startTime, session.endTime)}
                                                </span>
                                            </div>

                                            {/* Mostrar eventos reopened si existen */}
                                            {session.reopenedEvents && session.reopenedEvents.length > 0 && (
                                                <div style={{ 
                                                    marginTop: '0.75rem', 
                                                    paddingTop: '0.75rem', 
                                                    borderTop: '1px solid #e5e7eb' 
                                                }}>
                                                    <div style={{ 
                                                        fontSize: '0.875rem',
                                                        color: '#8b5cf6',
                                                        fontWeight: 600,
                                                        marginBottom: '0.5rem'
                                                    }}>
                                                        {session.reopenedEvents.length === 1 ? 'Reabierta:' : 'Reabiertas:'}
                                                    </div>
                                                    {session.reopenedEvents.map((reopenedEvent, idx) => (
                                                        <div key={idx} className="time-row" style={{ marginLeft: '0.5rem' }}>
                                                            <span className="time-value" style={{ color: '#8b5cf6', fontSize: '0.875rem' }}>
                                                                {formatSessionDateTime(reopenedEvent.date_time)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center mt-5 py-4">
                                    <p className="text-muted mb-0">
                                        No se encontraron sesiones para la tarea (ID: {resolvedTaskId})
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center mt-5">
                            <p className="text-muted">No se ha especificado una tarea</p>
                        </div>
                    )}
                </div>
            </div>

            <Modal_proyectos
                isOpen={assigneePickerOpen}
                onClose={() => {
                    if (reassigning) return;
                    setAssigneePickerOpen(false);
                    setAssigneeSearch('');
                }}
                title="Reasignar tarea"
            >
                <div className="modal-inner">
                    <div className="mb-3">
                        <div className="d-flex align-items-stretch border rounded overflow-hidden" style={{ fontSize: '0.875rem' }}>
                            <span className="d-flex align-items-center px-2 bg-white border-end text-muted">
                                <Search size={16} />
                            </span>
                            <input
                                type="search"
                                className="form-control border-0 shadow-none"
                                style={{ minWidth: 0 }}
                                placeholder="Buscar por nombre o correo…"
                                value={assigneeSearch}
                                onChange={(e) => setAssigneeSearch(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="list-group list-group-flush" style={{ maxHeight: 'min(55vh, 360px)', overflowY: 'auto' }}>
                        {filteredAssigneeOptions.length === 0 ? (
                            <p className="small text-muted mb-0 py-3 text-center">No hay miembros que coincidan.</p>
                        ) : (
                            filteredAssigneeOptions.map((opt) => (
                                <button
                                    key={opt.usr_id}
                                    type="button"
                                    className={`list-group-item list-group-item-action d-flex align-items-center gap-3 py-2 px-2${
                                        opt.usr_id === assigneeUsrId ? ' active' : ''
                                    }`}
                                    disabled={reassigning}
                                    onClick={() => handleSelectReassignMember(opt)}
                                >
                                    <UserAvatar usr_id={opt.usr_id} usr_name={opt.displayName} />
                                    <span className="text-start text-truncate flex-grow-1">{opt.displayName}</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            </Modal_proyectos>
        </div>
    );
};

export default Statistics;

